import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Dropdown } from "antd";
import CustomAvatar from "../../../Core/CommonV2/CustomAvatar";
import { downloadFiles } from "../../../Core/utils";
import getImageWeServeUrl from "../../../Core/utils/getImageWeServeUrl";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
    updateConversations,
    updateConversationsMultiple,
    resetConversations,
    starMessageLocally,
    updateReaction,
} from "../../../Core/redux/slices/conversations.slice";
import { addReaction } from "../../../Core/redux/api/v3/conversationAPI";
import { MessageReplyIconV3 } from "../../../Core/svgV2/MessageReplyIcon";
import { DownloadIcon } from "../../../Core/svgV2/DownloadIcon";
import { ForwardV3Icon } from "../../../Core/svgV2/ForwardIcon";
import { EmojiIconV3 } from "../../../Core/svgV2/EmojiIcon";
import StarIcon from "../../../Core/svgV3/StarIcon";
import UnstarIcon from "../../../Core/svgV3/UnstarIcon";
import { CrossIcon } from "../../../Core/svgV2/CrossIcon";
import { ChevronIcon } from "../../../Core/svgV2/Chevron";
import AppleEmoji from "../../../Core/Common/AppleEmoji";
import Picker, { EmojiStyle } from "emoji-picker-react";
import { REACTIONS } from "../../../Core/constants";

// ── Icons not exported elsewhere in the codebase ────────────────────────────
const FileGenericIcon = ({ className = "" }) => (
    <svg width="24" height="24" viewBox="0 0 20 20" fill="none" className={className}>
        <rect width="15" height="15" x="2.5" y="2.5" fill="#E0E7EE" rx="4" />
        <path
            stroke="#0D3271"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M10.833 5.946v1.721c0 .234 0 .35.046.44.04.078.104.142.182.182.089.045.206.045.44.045h1.72m-2.388 3.75h-2.5m3.334-1.667H8.333m5-1.255v3.005c0 .7 0 1.05-.136 1.318a1.25 1.25 0 01-.546.546c-.268.136-.617.136-1.318.136H8.667c-.7 0-1.05 0-1.318-.136a1.25 1.25 0 01-.546-.546c-.136-.268-.136-.618-.136-1.318V7.834c0-.7 0-1.05.136-1.317a1.25 1.25 0 01.546-.547c.268-.136.618-.136 1.318-.136h1.338c.306 0 .459 0 .602.035.128.03.25.08.362.15.126.076.234.185.45.4l1.329 1.33c.216.215.324.323.401.45.069.111.12.233.15.36.034.145.034.298.034.603z"
        />
    </svg>
);

const PlusIcon = ({ className = "" }) => (
    <svg width="15" height="15" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M7.63672 14.6562H12.998V20.0176C12.998 20.5625 13.4463 21.0195 14 21.0195C14.5537 21.0195 15.002 20.5625 15.002 20.0176V14.6562H20.3633C20.9082 14.6562 21.3652 14.208 21.3652 13.6543C21.3652 13.1006 20.9082 12.6523 20.3633 12.6523H15.002V7.29102C15.002 6.74609 14.5537 6.28906 14 6.28906C13.4463 6.28906 12.998 6.74609 12.998 7.29102V12.6523H7.63672C7.0918 12.6523 6.63477 13.1006 6.63477 13.6543C6.63477 14.208 7.0918 14.6562 7.63672 14.6562Z" fill="#667085" />
    </svg>
);

const formatModalTimestamp = (createdAt?: string) => {
    if (!createdAt) return "";
    const d = dayjs(createdAt).utc().local();
    if (d.isSame(dayjs(), "day")) return `Today at ${d.format("h:mm a")}`;
    if (d.isSame(dayjs().subtract(1, "day"), "day")) return `Yesterday at ${d.format("h:mm a")}`;
    return d.format("MMM DD [at] h:mm a");
};

/**
 * File preview overlay for a single conversation. Rendered as a plain
 * `absolute inset-0` layer INSIDE the sidebar+chatwindow wrapper (see
 * ConversationsScreen.tsx, which must have `relative` on that wrapper) —
 * not a viewport-centered dialog — so it fills exactly the space the
 * sidebar + chat window occupy (header, message list and composer all sit
 * underneath it) and never touches the app's global nav/header.
 *
 * `socket` is optional and only needed to broadcast an emoji reaction —
 * pass it down from ConversationsScreen the same way ChatWindow receives it.
 */
const ConversationFilePreviewModal = ({
    onStarMessage,
    socket,
}: {
    onStarMessage?: (message: any, isStarred: boolean) => void;
    socket?: any;
}) => {
    const dispatch = useAppDispatch();
    const { filePreview, currentThread, tenantMembers } = useAppSelector((state: any) => ({
        filePreview: state.conversations.filePreview,
        currentThread: state.conversations.currentThread,
        tenantMembers: state.tenant.tenantMembers,
    }));
    const { user } = useAppSelector((state: any) => state.userDetails);

    const { visible, docs = [], createdAt, message } = filePreview || {};
    const [activeIndex, setActiveIndex] = useState(filePreview?.currentIndex || 0);

    // ── Emoji reaction popover state (mirrors the quick-reaction bar in
    // Message.tsx: a row of REACTIONS + a "+" button that expands to the
    // full emoji-picker-react picker) ──────────────────────────────────────
    const [reactionOpen, setReactionOpen] = useState(false);
    const [showFullPicker, setShowFullPicker] = useState(false);

    useEffect(() => {
        if (visible) setActiveIndex(filePreview?.currentIndex || 0);
    }, [visible, filePreview?.currentIndex]);

    const close = () => dispatch(resetConversations(["filePreview"]));

    useEffect(() => {
        if (!visible) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowLeft") setActiveIndex((i) => Math.max(0, i - 1));
            if (e.key === "ArrowRight") setActiveIndex((i) => Math.min(docs.length - 1, i + 1));
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, docs.length]);

    if (!visible || !docs.length) return null;

    const activeDoc = docs[activeIndex] || docs[0];
    const type = activeDoc?.type || activeDoc?.mimeType || "";
    const isVideo = type.includes("video");
    const isImage = type.includes("image");

    const senderId = message?.senderId ?? message?.sender;
    const isOwn = senderId != null && senderId == user?.id;
    const senderName = isOwn ? "You" : message?.senderName || "Unknown";
    const resolvedMember = !isOwn ? tenantMembers?.find((t: any) => t.id == senderId) : null;
    const senderProfilePicture = isOwn
        ? user?.profilePicture
        : currentThread?.type === "private"
            ? currentThread?.profilePicture
            : resolvedMember?.profilePicture;

    const isStarred =
        Array.isArray(message?.starred) && message.starred.includes(String(user?.id));

    const goPrev = () => setActiveIndex((i) => (i > 0 ? i - 1 : i));
    const goNext = () => setActiveIndex((i) => (i < docs.length - 1 ? i + 1 : i));

    const handleReply = () => {
        if (!message) return close();
        dispatch(
            updateConversations({
                key: "replyMessage",
                value: { reply: true, message: message.message, messageObject: message },
            })
        );
        close();
    };

    const handleForward = () => {
        if (!message) return close();
        dispatch(
            updateConversationsMultiple([
                { key: "shareMessageModal", value: true },
                { key: "forwardMessage", value: { ...message, attachments: [activeDoc] } },
                { key: "shareType", value: "forwardMessage" },
            ])
        );
        close();
    };

    const handleStar = () => {
        if (!message) return;
        if (onStarMessage) {
            onStarMessage(message, isStarred);
            return;
        }
        dispatch(
            starMessageLocally({
                chatId: message.chat || message.threadId || currentThread?.threadId,
                messageId: message._id,
                userId: user?.id,
                starring: !isStarred,
            })
        );
    };

    // ── Emoji reaction ───────────────────────────────────────────────────────
    const isAlreadyReacted = (emoji: string) =>
        !!(message?.reactions && message.reactions[emoji] && message.reactions[emoji].includes(user?.id));

    const handleReact = (emoji: string) => {
        if (!message) return;
        const chatIdToUse = message.chat || message.threadId || currentThread?.threadId;
        const alreadyReacted = isAlreadyReacted(emoji);

        addReaction({ messageId: message._id, userId: user.id, emoji });

        socket?.emit("reaction", {
            emoji,
            userId: user.id,
            messageId: message._id,
            isAlreadyReacted: alreadyReacted,
            threadId: chatIdToUse,
            chatId: chatIdToUse,
        });

        dispatch(updateReaction({ messageId: message._id, userId: user.id, emoji, threadId: chatIdToUse }));

        setReactionOpen(false);
        setShowFullPicker(false);
    };

    const renderThumb = (doc: any, idx: number) => {
        const dType = doc?.type || doc?.mimeType || "";
        const selected = idx === activeIndex;
        const size = selected ? 60 : 40;
        const base =
            "relative flex-shrink-0 rounded-[4px] overflow-hidden cursor-pointer bg-white flex items-center justify-center transition-all duration-150 " +
            (selected ? "border-2 border-[#53B1FD]" : "border-0");
        const style = { width: size, height: size };

        if (dType.includes("image")) {
            return (
                <button key={idx} className={base} style={style} onClick={() => setActiveIndex(idx)}>
                    <img
                        src={getImageWeServeUrl(doc.url, { w: "120", h: "120" })}
                        className="w-full h-full object-cover"
                    />
                </button>
            );
        }
        if (dType.includes("video")) {
            return (
                <button key={idx} className={base} style={style} onClick={() => setActiveIndex(idx)}>
                    {doc.thumbnailUrl ? (
                        <img src={doc.thumbnailUrl} className="w-full h-full object-cover" />
                    ) : (
                        <img src="/images/v2/conversations/Video.svg" className="w-10 h-10" />
                    )}
                </button>
            );
        }
        return (
            <button key={idx} className={base} style={style} onClick={() => setActiveIndex(idx)}>
                <FileGenericIcon className="w-6 h-6" />
            </button>
        );
    };

    return (
        // Fills the sidebar+chatwindow wrapper exactly — that parent must
        // have `relative` for this to scope correctly instead of escaping
        // to the nearest positioned ancestor.
        <div className="absolute inset-0 z-40 bg-white flex flex-col">
            {/* Header — Figma: Height 80 Fixed, Horizontal, Padding 16/24, Gap 8, #F9FBFE */}
            <div
                className="flex items-center flex-shrink-0"
                style={{ height: 80, padding: "16px 24px", gap: 8, background: "#F9FBFE" }}
            >
                <CustomAvatar
                    title={senderName}
                    size={40}
                    src={senderProfilePicture}
                    whiteText
                    userId={senderId}
                />
                <div className="flex flex-col min-w-0 mr-auto ml-2">
                    <span className="out-500-14 text-gray-900 truncate">{senderName}</span>
                    <span className="out-300-12 text-gray-500 truncate">
                        {formatModalTimestamp(createdAt)}
                    </span>
                </div>

                {/* File action buttons — Figma: Hug 212 × Hug 20, Horizontal, Gap 12.
                    Order: Reply, React, Forward, Save to device, Star, Close. */}
                <div className="flex items-center flex-shrink-0" style={{ gap: 12 }}>
                    <button
                        className="w-5 h-5 flex items-center justify-center text-primary-600 hover:opacity-70"
                        onClick={handleReply}
                        title="Reply"
                    >
                        <MessageReplyIconV3 className="w-[18px] h-[18px]" />
                    </button>

                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: "r",
                                    label: showFullPicker ? (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            className="rounded-[12px] overflow-hidden"
                                            style={{
                                                "--epr-bg-color": "#F7FAFD",
                                                "--epr-category-label-bg-color": "#F7FAFD",
                                                "--epr-picker-border-color": "transparent",
                                                "--epr-search-input-bg-color": "#FFFFFF",
                                                "--epr-search-input-bg-color-active": "#FFFFFF",
                                                "--epr-search-border-color": "#EAECF0",
                                                "--epr-hover-bg-color": "#EAECF0",
                                                "--epr-focus-bg-color": "#EAECF0",
                                                "--epr-highlight-color": "#2E90FA",
                                                "--epr-category-icon-active-color": "#2E90FA",
                                                "--epr-text-color": "#344054",
                                                "--epr-header-padding": "8px 10px",
                                                "--epr-horizontal-padding": "10px",
                                            }}
                                        >
                                            <Picker
                                                emojiStyle={EmojiStyle.APPLE}
                                                lazyLoadEmojis
                                                width={292}
                                                height={291}
                                                previewConfig={{ showPreview: false }}
                                                onEmojiClick={(emojiObject) => handleReact(emojiObject.emoji)}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-x-2.5 py-3 px-3 w-full h-full">
                                            {REACTIONS.map((reaction) => (
                                                <button
                                                    key={reaction.reaction}
                                                    className="w-[26px] h-[26px] py-px flex-shrink-0 flex items-center justify-center hover:scale-125 transition-transform"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleReact(reaction.emoji);
                                                    }}
                                                >
                                                    <AppleEmoji emoji={reaction.emoji} size={24} />
                                                </button>
                                            ))}
                                            <button
                                                className="w-7 h-7 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition-colors"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setShowFullPicker(true);
                                                }}
                                            >
                                                <PlusIcon className="w-8 h-8" />
                                            </button>
                                        </div>
                                    ),
                                },
                            ],
                        }}
                        trigger={["click"]}
                        open={reactionOpen}
                        onOpenChange={(open) => {
                            setReactionOpen(open);
                            if (!open) setShowFullPicker(false);
                        }}
                        placement="bottomRight"
                        dropdownRender={(menu) => (
                            <div
                                className="w-fit h-[52px] flex items-center bg-[#F9FBFE] rounded-[12px]
                                shadow-[0px_12px_16px_-4px_rgba(16,24,40,0.08),0px_4px_6px_-2px_rgba(16,24,40,0.03)]
                                [&_.ant-dropdown-menu]:!p-0 [&_.ant-dropdown-menu]:!bg-transparent [&_.ant-dropdown-menu]:!shadow-none [&_.ant-dropdown-menu]:!rounded-[12px]
                                [&_.ant-dropdown-menu-item]:!p-0 [&_.ant-dropdown-menu-item]:!m-0
                                [&_.ant-dropdown-menu-item]:!bg-transparent
                                [&_.ant-dropdown-menu-item-active]:!bg-transparent
                                [&_.ant-dropdown-menu-item:hover]:!bg-transparent"
                            >
                                {menu}
                            </div>
                        )}
                    >
                        <button
                            className="w-5 h-5 flex items-center justify-center text-primary-600 hover:opacity-70"
                            title="React"
                        >
                            <EmojiIconV3 className="w-[18px] h-[18px]" />
                        </button>
                    </Dropdown>

                    <button
                        className="w-5 h-5 flex items-center justify-center text-primary-600 hover:opacity-70"
                        onClick={handleForward}
                        title="Forward"
                    >
                        <ForwardV3Icon className="w-[18px] h-[18px]" />
                    </button>

                    <button
                        className="w-5 h-5 flex items-center justify-center text-primary-600 hover:opacity-70"
                        onClick={() => downloadFiles([activeDoc.url], activeDoc.fileSize)}
                        title="Save to device"
                    >
                        <DownloadIcon width={18} height={18} />
                    </button>

                    <button
                        className="w-5 h-5 flex items-center justify-center hover:opacity-70"
                        onClick={handleStar}
                        title={isStarred ? "Unstar" : "Star"}
                    >
                        {isStarred ? (
                            <UnstarIcon className="w-[18px] h-[18px] text-yellow-500" />
                        ) : (
                            <StarIcon className="w-[18px] h-[18px] text-yellow-500" />
                        )}
                    </button>

                    <button
                        className="w-5 h-5 flex items-center justify-center text-gray-700 hover:opacity-70"
                        onClick={close}
                        title="Close"
                    >
                        <CrossIcon className="w-[18px] h-[18px]" />
                    </button>
                </div>
            </div>

            {/* Main preview — Figma: fill, bg Gray/100 #EDF3F9, padding 56/8/56/8, gap 16 */}
            <div
                className="relative flex-1 min-h-0 flex flex-col items-center justify-center"
                style={{
                    background: "#EDF3F9",
                    paddingTop: 56,
                    paddingBottom: 56,
                    paddingLeft: 8,
                    paddingRight: 8,
                    gap: 16,
                }}
            >
                {/* Nav arrows — Figma: 30 Hug × 30 Hug, radius 8px */}
                {docs.length > 1 && activeIndex > 0 && (
                    <button
                        className="absolute left-8 top-1/2 -translate-y-1/2 w-[30px] h-[30px] rounded-[8px] bg-white shadow-md border border-gray-100 flex items-center justify-center z-10 hover:bg-gray-50"
                        onClick={goPrev}
                    >
                        <ChevronIcon className="text-gray-600 rotate-90" />
                    </button>
                )}
                {docs.length > 1 && activeIndex < docs.length - 1 && (
                    <button
                        className="absolute right-8 top-1/2 -translate-y-1/2 w-[30px] h-[30px] rounded-[8px] bg-white shadow-md border border-gray-100 flex items-center justify-center z-10 hover:bg-gray-50"
                        onClick={goNext}
                    >
                        <ChevronIcon className="text-gray-600 -rotate-90" />
                    </button>
                )}

                <div className="flex-1 min-h-0 w-full flex items-center justify-center">
                    {isImage && (
                        <img
                            src={activeDoc?.imageOutOfBound ? activeDoc.url : getImageWeServeUrl(activeDoc.url, {})}
                            className="max-w-full max-h-full object-contain rounded"
                        />
                    )}
                    {isVideo && (
                        <video controls autoPlay={false} preload="metadata" className="max-w-full max-h-full rounded">
                            <source src={`${activeDoc.url}#t=0.1`} />
                        </video>
                    )}
                    {!isImage && !isVideo && (
                        <div className="flex flex-col items-center gap-y-3 text-gray-500">
                            <FileGenericIcon className="w-16 h-16" />
                            <span className="out-500-14">{activeDoc?.name || "No preview available"}</span>
                            <button
                                className="px-4 py-2 rounded-md bg-primary-600 text-white out-500-14"
                                onClick={() => downloadFiles([activeDoc.url], activeDoc.fileSize)}
                            >
                                Download File
                            </button>
                        </div>
                    )}
                </div>

                {(message?.message || message?.content) && (
                    <p className="out-300-12 text-gray-500 text-center max-w-[80%] truncate">
                        {message.message || message.content}
                    </p>
                )}
            </div>

            {/* Filmstrip — Figma: Fill width, Hug height (Max 100), Padding 20 top/bottom, Gap 8, #F9FBFE */}
            {docs.length > 1 && (
                <div
                    className="flex-shrink-0 flex items-center overflow-x-auto"
                    style={{ padding: "20px 24px", gap: 8, background: "#F9FBFE", maxHeight: 100 }}
                >
                    {docs.map((doc: any, idx: number) => renderThumb(doc, idx))}
                </div>
            )}
        </div>
    );
};

export default ConversationFilePreviewModal;